import {useEffect, useMemo, useState} from 'react';
import {Tooltip} from 'antd';
import {DownOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';

import {formatBalance} from 'utils/currencyFormatting';
import styles from '../wallet.module.scss';

const STORAGE_KEY = 'wallet.currencyFloat.collapsed';

/**
 * Collapsing is a per-viewer preference, so it lives in localStorage rather
 * than anywhere shared. Private windows and blocked site data make these throw,
 * hence the guards — the section just defaults to open when storage is unusable.
 */
const readCollapsed = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

/**
 * Float held per currency, summed across every provider that reports it.
 *
 * Summing is only valid within a single currency — XOF and USD are different
 * units — so this deliberately produces one tile per currency rather than a
 * single headline number. `paymentProviderActualBalance` is the money sitting
 * with the provider; `totalPlatformBalance` is Daba's own share of it.
 */
const buildBalances = providers => {
  const byCurrency = {};

  (providers ?? []).forEach(provider => {
    Object.entries(provider?.currencies ?? {}).forEach(([currency, value]) => {
      const providerBalance = value?.paymentProviderActualBalance;
      const platformBalance = value?.totalPlatformBalance;
      if (providerBalance == null && platformBalance == null) return;

      const entry = byCurrency[currency] ?? {
        currency,
        providerBalance: 0,
        platformBalance: 0,
        providerCount: 0,
      };
      entry.providerBalance += Number(providerBalance ?? 0);
      entry.platformBalance += Number(platformBalance ?? 0);
      entry.providerCount += 1;
      byCurrency[currency] = entry;
    });
  });

  return Object.values(byCurrency).sort(
    (a, b) => b.providerBalance - a.providerBalance,
  );
};

const CurrencyFloat = ({providers}) => {
  const balances = useMemo(() => buildBalances(providers), [providers]);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch (error) {
      // Storage is unavailable; the choice simply will not persist.
    }
  }, [collapsed]);

  if (!balances.length) return null;

  return (
    <div className={styles.balancesCard}>
      <div className={styles.balancesHeader}>
        <button
          type="button"
          className={styles.balancesToggle}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(current => !current)}>
          <DownOutlined
            className={`${styles.balancesChevron} ${
              collapsed ? styles.balancesChevronCollapsed : ''
            }`}
          />
          <h2 className={styles.balancesTitle}>Float held per currency</h2>
          <span className={styles.balancesCount}>{balances.length}</span>
        </button>
        <Tooltip title="Balances are only added up within a currency — different currencies are never combined into one figure.">
          <span className={styles.balancesHint}>
            Totalled across all providers
          </span>
        </Tooltip>
      </div>
      <div className={styles.balancesGrid} hidden={collapsed}>
        {balances.map(entry => (
          <div className={styles.balanceTile} key={entry.currency}>
            <span className={styles.balanceCurrency}>
              <span className={styles.balanceBadge}>{entry.currency}</span>
              {entry.providerCount} provider
              {entry.providerCount === 1 ? '' : 's'}
            </span>
            <span className={styles.balanceValue}>
              {formatBalance(entry.providerBalance, entry.currency)}
            </span>
            <span className={styles.balanceCaption}>
              {formatBalance(entry.platformBalance, entry.currency)} is
              Daba&apos;s
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

CurrencyFloat.propTypes = {
  providers: PropTypes.array,
};

export default CurrencyFloat;
